/*
  Product card image slider
  - moves with CSS transform (not scroll), so the image always sits exactly
    on the card edge, at any browser zoom
  - endless loop, arrows, dots, touch swipe
*/
if (!customElements.get('card-slider')) {
  customElements.define(
    'card-slider',
    class CardSlider extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        this.track = this.querySelector('[data-track]');
        if (!this.track) return;

        this.prevBtn = this.querySelector('[data-prev]');
        this.nextBtn = this.querySelector('[data-next]');
        this.dots = Array.from(this.querySelectorAll('[data-dot]'));

        const slides = Array.from(this.track.children);
        this.count = slides.length;

        if (this.count < 2) {
          if (slides[0]) slides[0].classList.add('is-current');
          this.classList.add('is-ready');
          return;
        }

        // Clones for the endless loop: [last-clone, 1, 2, ... n, first-clone]
        const firstClone = slides[0].cloneNode(true);
        const lastClone = slides[this.count - 1].cloneNode(true);
        [firstClone, lastClone].forEach((clone) => {
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('data-clone', '');
          clone.classList.remove('is-current');
        });
        this.track.prepend(lastClone);
        this.track.append(firstClone);
        this.allSlides = Array.from(this.track.children);

        this.index = 1; // position inside allSlides
        this.setPosition(false);

        // Arrows
        if (this.prevBtn) {
          this.prevBtn.disabled = false;
          this.prevBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.move(-1);
          });
        }
        if (this.nextBtn) {
          this.nextBtn.disabled = false;
          this.nextBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.move(1);
          });
        }

        this.track.addEventListener('transitionend', (e) => {
          if (e.target === this.track && e.propertyName === 'transform') this.finish();
        });

        this.bindTouch();

        // Don't open the product if the user was swiping
        this.addEventListener(
          'click',
          (e) => {
            if (this._dragged) {
              e.preventDefault();
              e.stopPropagation();
              this._dragged = false;
            }
          },
          true
        );

        this.markCurrent();
        this.updateDots();
        this.classList.add('is-ready');
      }

      get width() {
        return this.track.getBoundingClientRect().width || 1;
      }

      // Place the track; offsetPx is used while a finger is dragging
      setPosition(animate, offsetPx = 0) {
        const track = this.track;
        track.style.transition = animate ? '' : 'none';
        track.style.transform =
          offsetPx === 0
            ? `translateX(${-this.index * 100}%)`
            : `translateX(calc(${-this.index * 100}% + ${offsetPx}px))`;
        if (!animate) void track.offsetWidth;
      }

      animate() {
        this._animating = true;
        this.classList.add('is-moving');
        this.setPosition(true);
        this.updateDots();
        // Fallback in case transitionend doesn't fire (e.g. reduced motion)
        clearTimeout(this._fallback);
        this._fallback = setTimeout(() => {
          if (this._animating) this.finish();
        }, 600);
      }

      move(dir) {
        if (this._animating) this.finish();
        this.index += dir;
        this.animate();
      }

      // After a move: if we are on a clone, jump to the matching real slide
      finish() {
        clearTimeout(this._fallback);
        this._animating = false;
        if (this.index <= 0) this.index = this.count;
        else if (this.index >= this.count + 1) this.index = 1;
        this.setPosition(false);
        this.markCurrent();
        this.updateDots();
        this.classList.remove('is-moving');
      }

      markCurrent() {
        this.allSlides.forEach((slide, i) => slide.classList.toggle('is-current', i === this.index));
      }

      updateDots() {
        const real = (((this.index - 1) % this.count) + this.count) % this.count;
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === real));
      }

      bindTouch() {
        const track = this.track;

        track.addEventListener(
          'touchstart',
          (e) => {
            if (e.touches.length !== 1) return;
            if (this._animating) this.finish();
            this._dragged = false;
            this._startX = e.touches[0].clientX;
            this._startY = e.touches[0].clientY;
            this._dx = 0;
            this._axis = null;
          },
          { passive: true }
        );

        track.addEventListener(
          'touchmove',
          (e) => {
            if (e.touches.length !== 1) return;
            const dx = e.touches[0].clientX - this._startX;
            const dy = e.touches[0].clientY - this._startY;

            if (!this._axis) {
              if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
              this._axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
              if (this._axis === 'x') this.classList.add('is-moving');
            }
            if (this._axis !== 'x') return; // vertical: let the page scroll

            e.preventDefault();
            this._dx = dx;
            this.setPosition(false, dx);
          },
          { passive: false }
        );

        const end = () => {
          if (this._axis === 'x') {
            this._dragged = true;
            if (Math.abs(this._dx) > this.width * 0.15) {
              this.index += this._dx < 0 ? 1 : -1;
            }
            this.animate(); // to the new slide, or back to the same one
          }
          this._axis = null;
          this._dx = 0;
        };
        track.addEventListener('touchend', end, { passive: true });
        track.addEventListener('touchcancel', end, { passive: true });
      }
    }
  );
}